import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/store/Logo";

/* The verification page mirrors the physical certificate card: aubergine ground,
   white double-hairline frame, diamond lozenge dividers, centred layout — the
   digital twin of the card the customer is holding. */

const Lozenge = () => (
  <div className="flex items-center justify-center gap-3" aria-hidden="true">
    <span className="h-px w-14 bg-white/30" />
    <span className="w-1.5 h-1.5 rotate-45 border border-gold" />
    <span className="h-px w-14 bg-white/30" />
  </div>
);

const CardFrame = ({ children, testid }) => (
  <div className="border border-white/50 rounded-sm p-1.5" data-testid={testid}>
    <div className="border border-white/25 rounded-sm px-6 py-8 text-center space-y-3">
      {children}
    </div>
  </div>
);

export default function Verify() {
  const { serial: routeSerial } = useParams();
  const [serial, setSerial] = useState(routeSerial || "");
  const [result, setResult] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [busy, setBusy] = useState(false);

  const check = useCallback(async (s) => {
    if (!s.trim()) return;
    setBusy(true); setInvalid(""); setResult(null);
    try {
      const r = await api.get(`/verify/${s.trim()}`);
      setResult(r.data);
    } catch {
      setInvalid(s.trim().toUpperCase());
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { if (routeSerial) check(routeSerial).catch((e) => toast.error("Verification failed")); }, [routeSerial, check]);

  return (
    <div className="bg-brand text-ivory min-h-[80vh]" data-testid="verify-page">
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="text-center">
          <Logo on="dark" className="h-10 mx-auto" />
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-white/60 mt-2">Jewels of Love</p>
          <h1 className="font-heading text-2xl font-semibold mt-6">Certificate verification</h1>
          <p className="text-white/60 text-sm mt-2">Every Siyaara coin carries a serial and a QR on its card. Enter the serial printed beside the QR to verify authenticity.</p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); check(serial); }} className="mt-8 flex gap-2">
          <Input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="e.g. SIY-2609-00100"
            className="font-mono h-11 bg-white/10 border-white/30 text-ivory placeholder:text-white/40" data-testid="verify-input" />
          <Button type="submit" disabled={busy} className="bg-gold text-[#372248] hover:bg-gold/90 rounded-full px-6 h-11 btn-press font-semibold" data-testid="verify-submit">
            {busy ? "…" : "Verify"}
          </Button>
        </form>

        {invalid && (
          <div className="mt-8 animate-fade-up">
            <CardFrame testid="verify-invalid">
              <ShieldX size={36} className="mx-auto text-red-300" />
              <p className="font-heading font-semibold text-lg">Not a valid Siyaara certificate</p>
              <Lozenge />
              <p className="text-sm text-white/70">Serial <span className="font-mono">{invalid}</span> does not match any certificate we have issued.</p>
              <p className="text-sm text-white/70">Check the serial on your card and try again. If it still does not verify, do not accept the piece — contact us at <a href="mailto:hello@siyaara.com" className="text-gold underline">hello@siyaara.com</a> or +91 73046 75916 and we will check it for you.</p>
            </CardFrame>
          </div>
        )}

        {result && (
          <div className="mt-8 animate-fade-up">
            <CardFrame testid="verify-valid">
              <Logo on="dark" className="h-8 mx-auto" />
              <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-white/60">Jewels of Love</p>
              {result.product_line && (
                <p className="inline-block border border-gold text-gold text-[10px] font-mono tracking-[0.25em] uppercase px-3 py-1 rounded-full" data-testid="verify-product-line">{result.product_line}</p>
              )}
              <Lozenge />
              <p className="font-heading text-lg" data-testid="verify-fineness">{result.fineness_label || `${result.metal} Fineness : ${result.fineness}`}</p>
              <p className="font-heading text-lg" data-testid="verify-weight">{result.weight_label || `${result.metal} Weight : ${result.weight} gms.`}</p>
              <p className="text-sm text-white/80" data-testid="verify-crafted">{result.crafted}</p>
              <p className="text-sm text-white/80" data-testid="verify-tolerance">Tolerance : {result.tolerance}</p>
              {result.design && <p className="text-sm text-white/60">Motif : {result.design}</p>}
              <Lozenge />
              <p className="text-xs text-white/60">Certificate serial number</p>
              <p className="font-mono text-base tracking-wider" data-testid="verify-serial">{result.serial}</p>
              <p className="text-xs text-white/60">Date of issue</p>
              <p className="text-sm" data-testid="verify-issued">{result.issue_date || fmtDateTime(result.created_at)}</p>
              <Lozenge />
              <div className="flex items-center justify-center gap-2 text-gold">
                <ShieldCheck size={18} />
                <p className="font-heading font-semibold" data-testid="verify-authentic">Verified — authentic Siyaara piece</p>
              </div>
              <p className="text-[11px] text-white/50 pt-1">
                <a href="https://siyaara.com" className="underline">siyaara.com</a> · <a href="/policies/terms" className="underline">Terms &amp; conditions</a>
              </p>
            </CardFrame>
          </div>
        )}
      </div>
    </div>
  );
}
